import { useEffect, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { Button } from "@repo/blacksmith-ui/button";
import { Dialog } from "@repo/blacksmith-ui/dialog";
import { assetUrl } from "@/lib/assetUrl";
import { cn } from "@repo/blacksmith-ui/cn";

export const CODESMITH_INTRO_VIDEO_SEEN_STORAGE_KEY =
  "codesmith-intro-video-seen";

// Promo clip served from dashboard/public/assets (native 1200×680).
const INTRO_VIDEO_URL = assetUrl("codesmith-promo-in-app2.mp4");

/**
 * Warm the browser cache for the intro promo video from any dashboard page,
 * so the clip is already fetched by the time a first-time user opens the
 * Codesmith tab. Skipped once the seen key is set (the modal will never
 * show again) and deferred to idle time so it can't compete with page load.
 */
export function useCodesmithIntroVideoPrefetch() {
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    try {
      if (
        window.localStorage.getItem(CODESMITH_INTRO_VIDEO_SEEN_STORAGE_KEY) ===
        "true"
      ) {
        return;
      }
    } catch {
      // localStorage can throw in private mode; prefetch anyway.
    }

    const prefetch = () => {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.as = "video";
      link.href = INTRO_VIDEO_URL;
      document.head.appendChild(link);
    };

    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(prefetch, { timeout: 5000 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timeoutId = window.setTimeout(prefetch, 2000);
    return () => window.clearTimeout(timeoutId);
  }, []);
}

/**
 * Whether the intro video modal should auto-open for this visit. The pool
 * gates mirror the onboarding modal's: only auto-pop for a user who has
 * never created a session, and never over a PR-link or task-first entry or
 * while a superuser is impersonating a foreign org.
 */
export function shouldShowIntroVideoModal({
  hasSeenIntroVideo,
  userHasNoOwnSessions,
  hasLinkedPrFromUrl,
  skipIntroVideo,
  isImpersonating,
}: {
  hasSeenIntroVideo: boolean;
  userHasNoOwnSessions: boolean;
  hasLinkedPrFromUrl: boolean;
  skipIntroVideo: boolean;
  isImpersonating: boolean;
}): boolean {
  return (
    !hasSeenIntroVideo &&
    userHasNoOwnSessions &&
    !hasLinkedPrFromUrl &&
    !skipIntroVideo &&
    !isImpersonating
  );
}

function IntroVideo() {
  // Browsers only autoplay muted videos, and React's JSX `muted` attribute
  // doesn't reliably set the DOM property on first render. Force it on via a
  // ref and kick play() so the clip animates instead of a black first frame.
  const videoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {});
    }
  }, []);

  return (
    <div
      className="relative w-full overflow-hidden bg-blacksmith-yellow"
      style={{ aspectRatio: "1200 / 680" }}
    >
      <video
        ref={videoRef}
        src={INTRO_VIDEO_URL}
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        aria-label="[code]smith demo"
      />
    </div>
  );
}

/**
 * First-visit intro modal that plays the Codesmith promo video. Uses a
 * viewport-wide blurred backdrop so everything behind it — including the
 * app sidebar rail — dims consistently.
 */
export function CodesmithIntroVideoModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 flex w-[min(560px,calc(100%-2rem))] max-w-none translate-x-[-50%] translate-y-[-50%] flex-col gap-0 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-app-background)] p-0 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
          // Radix auto-focuses the first focusable descendant on open, which
          // paints a focus ring on the close button before any interaction.
          onOpenAutoFocus={(event) => event.preventDefault()}
          // An outside click would dismiss the dialog — and the close handler
          // would mark the video seen. Only explicit closes (Get started, X,
          // Escape) dismiss; leaving the draft closes it silently upstream.
          onInteractOutside={(event) => event.preventDefault()}
          onFocusOutside={(event) => event.preventDefault()}
        >
          <IntroVideo />
          <div className="flex flex-col items-start gap-2 p-5 pb-6 text-left">
            <DialogPrimitive.Title className="type-body-m-medium m-0 text-neutral-900 dark:text-neutral-50">
              Meet [code]smith
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="type-body-s-regular m-0 text-neutral-600 dark:text-neutral-400">
              Agents shouldn&apos;t die when your laptop lid closes: [code]smith
              builds in the cloud, takes delegation from Slack, and keeps PRs
              green by fixing CI, answering reviewers, and resolving conflicts.
              <strong className="mt-2 block font-semibold text-neutral-900 dark:text-neutral-50">
                [code]smith is a paid product and is not covered under
                Blacksmith&apos;s free usage quota.
              </strong>
            </DialogPrimitive.Description>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button onClick={() => onOpenChange(false)}>Get started</Button>
              <Button asChild variant="secondary">
                <a
                  href="https://docs.blacksmith.sh/codesmith/overview"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View docs
                </a>
              </Button>
            </div>
          </div>
          <DialogPrimitive.Close
            className="absolute right-4 top-4 rounded-xs opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus-visible:outline-none border border-transparent focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0 disabled:pointer-events-none dark:ring-offset-neutral-950"
            aria-label="Close"
          >
            <X className="h-4 w-4 text-white" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}
